import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../elements/ui/card';
import { AnimatedNumber, busyDimClass, RevealCard, ValueFlash } from '../../elements/ui/motion';
import { Skeleton } from '../../elements/ui/skeleton';
import { FarmsOverview } from '../../models/farms-overview';
import { pigletRegistrationColors as c } from '../PigletRegistration/colors';
import { WeightSplitCard } from '../PigletRegistration/PigletStatCards';

const num = (value: number) => value.toLocaleString();
const pct = (value: number) => `${Math.round(value)}%`;

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4';

interface KpiCardProps {
  label: string;
  value: number;
  caption: string;
  dotColor: string;
  formatValue?: (value: number) => string;
  delayMs?: number;
  /** The headline number of the screen gets the larger type; the supporting ones do not. */
  hero?: boolean;
}

const KpiCard = ({ label, value, caption, dotColor, formatValue = num, delayMs = 0, hero }: KpiCardProps) => (
  <RevealCard delayMs={delayMs} className="h-full">
    {(revealed) => (
      <CardContent className="p-5">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className={`mt-2 font-semibold tracking-tight text-gray-900 tabular-nums ${hero ? 'text-4xl' : 'text-3xl'}`}>
          <ValueFlash value={value}>
            <AnimatedNumber value={value} format={formatValue} active={revealed} delayMs={delayMs + 100} />
          </ValueFlash>
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
          {caption}
        </p>
      </CardContent>
    )}
  </RevealCard>
);

/** Mirrors the card line for line so the rows do not jump height when the data lands. */
const KpiCardSkeleton = () => (
  <Card className="h-full">
    <CardContent className="p-5">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="mt-2 h-9 w-20" />
      <Skeleton className="mt-2 h-4 w-40" />
    </CardContent>
  </Card>
);

/** The split card is the tallest of the eight and sets its row's height, so it needs its own shape. */
const WeightSplitCardSkeleton = () => (
  <Card className="h-full">
    <CardContent className="p-5">
      <Skeleton className="h-4 w-28" />
      <Skeleton className="mt-3 h-2 w-full rounded-full" />

      <div className="mt-3 grid gap-1">
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-4 w-full" />
        ))}
      </div>

      <Skeleton className="mt-3 h-4 w-32" />
    </CardContent>
  </Card>
);

export interface FarmsOverviewKpisProps {
  overview?: FarmsOverview | null;
  isBusy: boolean;
}

/**
 * Two rows that make the queue below readable.
 *
 * The first row counts *farms*: how many there are, how much work there is, how many have gone
 * quiet, and what the shortfall adds up to. The second counts *piglets*: what the estate is actually
 * producing, and how well. They are deliberately not interleaved - "11 farms" and "1,204 g" answer different
 * questions, and a reader scanning one row should not have to keep switching unit.
 *
 * The shortfall and the silent farms are scored over each farm's last completed production cycles - enough of
 * them to span at least `lookbackWeeks` weeks - so the captions name cycles rather than a number of weeks.
 *
 * Every figure is over *monitored* farms - active farms with an expected weekly volume configured. The ones
 * without are reported under the first row rather than folded in, since a missing configuration is not a
 * farm to phone.
 */
export default function FarmsOverviewKpis({ overview, isBusy }: FarmsOverviewKpisProps) {
  const { t } = useTranslation();

  // Skeletons only until the first answer: after that a refetch dims the figures it is about to replace,
  // so the cards stay mounted and a changed figure counts across to its new value instead of from zero.
  if (!overview) {
    return (
      <div className="grid gap-4">
        <div className={GRID}>
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
        </div>
        <div className={GRID}>
          <KpiCardSkeleton />
          <WeightSplitCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
        </div>
      </div>
    );
  }

  const { summary, lookbackWeeks } = overview;

  return (
    <div className={busyDimClass(isBusy)}>
      <div className={GRID}>
        {/* The estate's size, so the counts beside it have a denominator to be read against. Summed from
            the two halves the API reports rather than sent as its own figure: every active farm is either
            monitored or missing an expected weekly volume. */}
        <KpiCard
          label={t('f-farms-overview-kpi-total')}
          value={summary.farmsMonitored + summary.farmsNotConfigured}
          caption={t('f-farms-overview-kpi-total-caption', {
            monitored: num(summary.farmsMonitored),
            notConfigured: num(summary.farmsNotConfigured)
          })}
          dotColor={c.registered}
        />

        <KpiCard
          hero
          delayMs={70}
          label={t('f-farms-overview-kpi-attention')}
          value={summary.farmsNeedingAttention}
          caption={t('f-farms-overview-kpi-attention-caption', {
            critical: num(summary.criticalCount),
            warning: num(summary.warningCount)
          })}
          dotColor={c.belowExpected}
        />

        <KpiCard
          delayMs={140}
          label={t('f-farms-overview-kpi-not-registering')}
          value={summary.farmsNotRegistering}
          caption={t('f-farms-overview-kpi-not-registering-caption-cycles')}
          dotColor={c.target}
        />

        <KpiCard
          delayMs={210}
          label={t('f-farms-overview-kpi-unaccounted')}
          value={summary.pigletsUnaccountedFor}
          caption={t('f-farms-overview-kpi-unaccounted-caption-cycles', { weeks: lookbackWeeks })}
          dotColor={c.target}
        />
      </div>

      {/* Sits between the rows, where it explains the farm counts above it rather than the piglet figures
          below. Without it those four numbers simply do not add up to the farms on the platform. */}
      {summary.farmsNotConfigured > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {t('f-farms-overview-not-configured', { count: summary.farmsNotConfigured })}
        </p>
      )}

      <div className={`${GRID} mt-4`}>
        {/* The caption names the weighed count rather than the total: an estate that weighs a third of its
            piglets gets an average built mostly from weight-category estimates, and should see that. */}
        <KpiCard
          label={t('f-farms-overview-kpi-birth-weight')}
          value={summary.averageBirthWeightGrams}
          formatValue={(value) => t('f-piglets-reg-grams', { value: num(value) })}
          caption={t('f-farms-overview-kpi-birth-weight-caption', {
            weighed: num(summary.weighedCount),
            registered: num(summary.totalRegistered)
          })}
          dotColor={c.target}
        />

        {/* The same card the single-farm dashboard uses, so the estate and one farm read the same way. */}
        <WeightSplitCard
          light={summary.lightPercent}
          medium={summary.mediumPercent}
          heavy={summary.heavyPercent}
          classified={summary.classifiedCount}
        />

        <KpiCard
          delayMs={140}
          label={t('f-farms-overview-kpi-mortality')}
          value={Math.round(summary.mortalityPercent)}
          formatValue={pct}
          caption={t('f-farms-overview-kpi-mortality-caption', {
            dead: num(summary.deadCount),
            registered: num(summary.totalRegistered)
          })}
          dotColor={c.belowExpected}
        />

        {/* Can exceed 100%: a piglet can be injected more than once, and an injection can land on a piglet
            registered before the period started. The caption says "of registered" rather than "treated" so
            the number is not misread as coverage. */}
        <KpiCard
          delayMs={210}
          label={t('f-farms-overview-kpi-injections')}
          value={summary.injectionCount}
          caption={t('f-farms-overview-kpi-injections-caption', {
            percent: pct(summary.injectionsPerRegisteredPercent)
          })}
          dotColor={c.registered}
        />
      </div>
    </div>
  );
}
